/**
 * Batched country rendering with edge case handling
 * Phase 4.2: Country Rendering System
 */

import { drawPolygon } from './polygon-renderer.js';
import { processPathEdgeCases } from './edge-cases.js';

/**
 * Render all countries with proper edge case handling
 * Matches Python logic from SVGModel.projected_paths()
 *
 * @param {Object} renderer - Renderer instance from createRenderer()
 * @param {Map} countries - Country data from Phase 3
 * @param {Object} earthModel - EarthModel instance with transformation methods
 * @param {Map} graphicsCache - Cache of Graphics objects per country (fixes memory leak)
 * @param {Map} rotationCache - Pre-computed rotated 3D coordinates (performance optimization)
 * @param {string|null} hoveredCountryId - Country to highlight with an outline
 * @param {Map|null} hitTestCache - Populated with screen polygons for hover hit testing
 */
export function renderAllCountries(renderer, countries, earthModel, graphicsCache, rotationCache, hoveredCountryId = null, hitTestCache = null) {
    const { layers, PIXI } = renderer;

    // Reset hit test cache for this frame
    if (hitTestCache) hitTestCache.clear();

    // Render each country using cached Graphics objects (reuse pattern)
    for (const [countryId, country] of countries) {
        // Get or create Graphics object for this country
        let graphics = graphicsCache.get(countryId);

        if (!graphics) {
            // First time: create new Graphics object and cache it
            graphics = new PIXI.Graphics();
            graphics.interactive = false;
            graphics.name = country.name; // For debugging
            graphicsCache.set(countryId, graphics);
            layers.countries.addChild(graphics);
        }

        // Clear previous frame's drawing (fast operation, no memory allocation)
        graphics.clear();

        // Get pre-rotated paths from cache (avoids duplicate rotation)
        const rotatedPaths = rotationCache.get(countryId);

        // Collect screen polygons for hit testing and hover outline
        const screenPolygons = [];

        // Redraw all paths for this country
        for (let i = 0; i < country.paths.length; i++) {
            // Step 1: Transform rotated 3D to normalized 2D [0,1] using projection
            const normalized2D = earthModel.transformRotatedToNormalized2D(rotatedPaths[i]);

            // Step 2: Process edge cases (matching Python's projected_paths logic)
            // Uses normalized coordinates: halfWidth=0.5, width=1.0, halfHeight=0.5, height=1.0
            const polygons = processPathEdgeCases(normalized2D, 0.5, 1.0, 0.5, 1.0);

            // Step 3: Transform each resulting polygon to screen coordinates and draw
            for (const polygon of polygons) {
                const screenCoords = earthModel.transformNormalized2DToScreen(
                    polygon,
                    renderer.width,
                    renderer.height
                );
                drawPolygon(graphics, screenCoords, country.color, false);
                screenPolygons.push(screenCoords);
            }
        }

        // Store screen polygons for hit testing
        if (hitTestCache) hitTestCache.set(countryId, screenPolygons);

        // Draw hover outline on top of fill for the hovered country
        if (countryId === hoveredCountryId) {
            for (const screenCoords of screenPolygons) {
                if (screenCoords.length < 4) continue;
                graphics.lineStyle(2, 0xffffff, 0.85);
                graphics.moveTo(screenCoords[0], screenCoords[1]);
                for (let j = 2; j < screenCoords.length; j += 2) {
                    graphics.lineTo(screenCoords[j], screenCoords[j + 1]);
                }
                graphics.closePath();
            }
            graphics.lineStyle(0); // Reset line style
        }
    }
}
